import Alpine from 'alpinejs';
import { getCSRFToken } from '../utils/getCSRFToken';
import Quill from 'quill';

export default function registerCalendarSettingsMessagingStore() {
  const existing = Alpine.store('calendarSettingsMessagingStore');
  if (existing) return existing;

  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  // Clone with property descriptors to preserve shared base behavior.
  const messagingStore = {};
  Object.defineProperties(
    messagingStore,
    Object.getOwnPropertyDescriptors(baseStore),
  );

  const messagingExtensions = {
    // Emails tab (Calendar Settings)
    activeEmailTab: 'templates',
    activeChannelTab: 'partners',
    emailTemplates: [],
    emailSchedules: [],
    emailScheduleRateSets: [],
    emailSchedulePartners: [],
    showEmailTemplateEditor: false,
    showEmailScheduleEditor: false,
    savingEmailTemplate: false,
    sendingEmailTemplateTest: false,
    savingEmailSchedule: false,
    currentEmailTemplate: {
      id: null,
      name: '',
      subject_template: '',
      body_html: '',
      attachment_name: '',
      attachment_mime_type: '',
      attachment_size: 0,
      attachment_url: '',
      is_active: true,
    },
    emailTemplatePendingAttachmentFile: null,
    emailTemplateRemoveAttachment: false,
    emailTemplateQuill: null,
    emailTemplateActiveField: 'body',
    emailTemplateLastSubjectSelectionStart: null,
    emailTemplateLastSubjectSelectionEnd: null,
    emailTemplateLastBodySelectionIndex: null,
    emailTemplateVariables: [
      { key: 'guest_first_name', label: 'Guest first name' },
      { key: 'guest_last_name', label: 'Guest last name' },
      { key: 'guest_full_name', label: 'Guest full name' },
      { key: 'business_name', label: 'Business name' },
      { key: 'booking_number', label: 'Booking number' },
      { key: 'start_date', label: 'Start date' },
      { key: 'end_date', label: 'End date' },
      { key: 'total_price', label: 'Total price' },
      { key: 'amount_due', label: 'Amount due' },
      { key: 'amount_due_20', label: 'Amount due (20%)' },
      { key: 'payment_link_amount', label: 'Payment link amount' },
      {
        key: 'payment_link_expiry_details',
        label: 'Payment link expiry details',
      },
      {
        key: 'payment_link',
        label: 'Payment link (generated by schedule settings)',
      },
      {
        key: 'digital_waiver',
        label: 'Digital waiver button/link',
      },
      { key: 'payment_request_note', label: 'Payment request note' },
      { key: 'booking_summary_html', label: 'Booking summary (HTML)' },
      { key: 'booking_message', label: 'Booking message' },
    ],
    currentEmailSchedule: {
      id: null,
      name: '',
      template: '',
      trigger_type: 'after_booked',
      trigger_anchor: 'booked',
      trigger_timing: 'after',
      offset_days: 0,
      trigger_from_statuses: [],
      allowed_booking_statuses: [],
      payment_link_mode: 'none',
      payment_link_provider: 'stripe',
      payment_link_fixed_amount: '',
      payment_link_balance_percentage: '',
      payment_link_expiry_days: 7,
      cancel_pending_on_payment_link_expiry: false,
      notify_staff_on_digital_waiver_signed: false,
      apply_to_all_ratesets: true,
      apply_to_all_partners: true,
      rate_set_ids: [],
      include_no_partner: true,
      included_partner_ids: [],
      excluded_partner_ids: [],
      is_active: true,
    },
    get templates() {
      return this.emailTemplates;
    },
    get schedules() {
      return this.emailSchedules;
    },
    get scheduleRateSets() {
      return this.emailScheduleRateSets;
    },
    get schedulePartners() {
      return this.emailSchedulePartners;
    },
    get showTemplateEditor() {
      return this.showEmailTemplateEditor;
    },
    set showTemplateEditor(value) {
      this.showEmailTemplateEditor = value;
    },
    get showScheduleEditor() {
      return this.showEmailScheduleEditor;
    },
    set showScheduleEditor(value) {
      this.showEmailScheduleEditor = value;
    },
    get savingTemplate() {
      return this.savingEmailTemplate;
    },
    get sendingTemplateTest() {
      return this.sendingEmailTemplateTest;
    },
    get isStripeConnectedForPayments() {
      return !!(
        this.stripeAccountStatus &&
        this.stripeAccountStatus.connected &&
        this.stripeAccountStatus.charges_enabled
      );
    },
    get isPaypalConnectedForPayments() {
      return this.paypalAccountComplete === true;
    },
    get hasAnyPaymentProviderForLinks() {
      return (
        this.isStripeConnectedForPayments || this.isPaypalConnectedForPayments
      );
    },
    get selectedPaymentLinkProvider() {
      return (
        String(this.currentEmailSchedule?.payment_link_provider || 'stripe')
          .trim()
          .toLowerCase() || 'stripe'
      );
    },
    get isSelectedPaymentProviderConnected() {
      if (this.selectedPaymentLinkProvider === 'paypal') {
        return this.isPaypalConnectedForPayments;
      }
      return this.isStripeConnectedForPayments;
    },
    get savingSchedule() {
      return this.savingEmailSchedule;
    },
    get editingTemplate() {
      return this.currentEmailTemplate;
    },
    set editingTemplate(value) {
      this.currentEmailTemplate = value;
    },
    get editingSchedule() {
      return this.currentEmailSchedule;
    },
    set editingSchedule(value) {
      this.currentEmailSchedule = value;
    },
    messagingInitialLoading: true,
    messagingInitialLoaded: false,
    _messagingInitPromise: null,

    async init() {
      if (this.messagingInitialLoaded) {
        this.messagingInitialLoading = false;
        return;
      }
      if (this._messagingInitPromise) {
        return this._messagingInitPromise;
      }

      this.messagingInitialLoading = true;
      this._messagingInitPromise = (async () => {
        try {
          this.initializeSettingsContext();
          this.fetchStripeAccountStatus();
          await Promise.allSettled([
            this.fetchEmailTemplates(),
            this.fetchEmailSchedules(),
          ]);
          this.messagingInitialLoaded = true;
        } finally {
          this.messagingInitialLoading = false;
          this._messagingInitPromise = null;
        }
      })();

      return this._messagingInitPromise;
    },

    // =====================================================================
    // EMAIL TEMPLATES + SCHEDULES
    // =====================================================================
    async fetchEmailTemplates() {
      try {
        const response = await fetch('/calendar/api/email-templates/');
        if (!response.ok) throw new Error('Failed to fetch email templates');
        this.emailTemplates = await response.json();
      } catch (error) {
        console.error('Error fetching email templates:', error);
      }
    },

    async fetchEmailSchedules() {
      try {
        const response = await fetch('/calendar/api/email-schedules/');
        if (!response.ok) throw new Error('Failed to fetch email schedules');
        const data = await response.json();
        this.emailSchedules = data.schedules || [];
        this.emailScheduleRateSets = data.rate_sets || [];
        this.emailSchedulePartners = data.partners || [];
      } catch (error) {
        console.error('Error fetching email schedules:', error);
      }
    },

    openNewEmailTemplateEditor() {
      this.currentEmailTemplate = {
        id: null,
        name: '',
        subject_template: '',
        body_html: '',
        attachment_name: '',
        attachment_mime_type: '',
        attachment_size: 0,
        attachment_url: '',
        is_active: true,
      };
      this.emailTemplatePendingAttachmentFile = null;
      this.emailTemplateRemoveAttachment = false;
      this.showEmailTemplateEditor = true;
      this.emailTemplateActiveField = 'body';
      this.initEmailTemplateQuill(true);
      setTimeout(() => {
        const el = document.getElementById('emailTemplateEditorCard');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },

    prepareNewTemplate() {
      this.openNewEmailTemplateEditor();
    },

    openEditEmailTemplateEditor(template) {
      this.currentEmailTemplate = {
        id: template.id,
        name: template.name,
        subject_template: template.subject_template,
        body_html: template.body_html,
        attachment_name: template.attachment_name || '',
        attachment_mime_type: template.attachment_mime_type || '',
        attachment_size: Number(template.attachment_size || 0),
        attachment_url: template.attachment_url || '',
        is_active: template.is_active,
      };
      this.emailTemplatePendingAttachmentFile = null;
      this.emailTemplateRemoveAttachment = false;
      this.showEmailTemplateEditor = true;
      this.emailTemplateActiveField = 'body';
      this.initEmailTemplateQuill(true);
      setTimeout(() => {
        const el = document.getElementById('emailTemplateEditorCard');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },

    setEmailTemplateActiveField(field) {
      this.emailTemplateActiveField = field;
    },

    captureEmailTemplateSubjectSelection() {
      const input = document.getElementById('emailTemplateSubjectInput');
      if (!input) return;
      this.emailTemplateLastSubjectSelectionStart = Number.isInteger(
        input.selectionStart,
      )
        ? input.selectionStart
        : null;
      this.emailTemplateLastSubjectSelectionEnd = Number.isInteger(
        input.selectionEnd,
      )
        ? input.selectionEnd
        : null;
    },

    initEmailTemplateQuill(forceSyncContent = false) {
      setTimeout(() => {
        const editor = document.getElementById('emailTemplateEditor');
        if (!editor) return;

        if (this.emailTemplateQuill) {
          if (forceSyncContent) {
            this.emailTemplateQuill.root.innerHTML =
              this.currentEmailTemplate.body_html || '';
          }
          return;
        }

        this.emailTemplateQuill = new Quill('#emailTemplateEditor', {
          theme: 'snow',
          modules: {
            toolbar: this.toolbarOptions,
          },
        });
        this.emailTemplateQuill.on('selection-change', (range) => {
          if (range) {
            this.emailTemplateActiveField = 'body';
            this.emailTemplateLastBodySelectionIndex = range.index;
          }
        });
        this.emailTemplateQuill.root.innerHTML =
          this.currentEmailTemplate.body_html || '';
      }, 0);
    },

    insertTemplateVariable(variableKey) {
      const variableToken = `{{${variableKey}}}`;

      if (this.emailTemplateActiveField === 'subject') {
        const input = document.getElementById('emailTemplateSubjectInput');
        const current = this.currentEmailTemplate.subject_template || '';

        if (!input) {
          this.currentEmailTemplate.subject_template = `${current}${variableToken}`;
          return;
        }

        const hasLiveSelection =
          Number.isInteger(input.selectionStart) &&
          Number.isInteger(input.selectionEnd) &&
          document.activeElement === input;
        const start = hasLiveSelection
          ? input.selectionStart
          : Number.isInteger(this.emailTemplateLastSubjectSelectionStart)
          ? this.emailTemplateLastSubjectSelectionStart
          : current.length;
        const end = hasLiveSelection
          ? input.selectionEnd
          : Number.isInteger(this.emailTemplateLastSubjectSelectionEnd)
          ? this.emailTemplateLastSubjectSelectionEnd
          : current.length;
        const nextValue =
          current.slice(0, start) + variableToken + current.slice(end);

        this.currentEmailTemplate.subject_template = nextValue;
        input.focus();
        const caret = start + variableToken.length;
        input.setSelectionRange(caret, caret);
        this.emailTemplateLastSubjectSelectionStart = caret;
        this.emailTemplateLastSubjectSelectionEnd = caret;
        return;
      }

      this.initEmailTemplateQuill(false);
      if (!this.emailTemplateQuill) return;

      const root = this.emailTemplateQuill.root;
      if (!root) return;

      const selection = window.getSelection();
      let insertedAtCaret = false;

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const owner =
          container?.nodeType === 3 ? container.parentNode : container;
        const isInsideEditor =
          owner && (root === owner || root.contains(owner));

        if (isInsideEditor) {
          range.deleteContents();
          const textNode = document.createTextNode(variableToken);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(range);
          insertedAtCaret = true;
        }
      }

      if (!insertedAtCaret) {
        // Fallback: append at end when no valid caret is available.
        const textNode = document.createTextNode(variableToken);
        root.appendChild(textNode);
        if (selection) {
          const endRange = document.createRange();
          endRange.setStartAfter(textNode);
          endRange.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(endRange);
        }
      }

      this.currentEmailTemplate.body_html = root.innerHTML;
      this.emailTemplateLastBodySelectionIndex = null;
      this.emailTemplateActiveField = 'body';
    },

    editTemplate(template) {
      this.openEditEmailTemplateEditor(template);
    },

    formatEmailTemplateAttachmentSize(sizeBytes) {
      const size = Number(sizeBytes || 0);
      if (!size) return '';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },

    async processImageAttachmentForTemplate(file, maxBytes = 2 * 1024 * 1024) {
      const imageUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = imageUrl;
      });
      const canvas = document.createElement('canvas');
      const maxDimension = 2200;
      const scale = Math.min(
        1,
        maxDimension / Math.max(image.width || 1, image.height || 1),
      );
      canvas.width = Math.max(1, Math.floor(image.width * scale));
      canvas.height = Math.max(1, Math.floor(image.height * scale));
      const context = canvas.getContext('2d', { alpha: true });
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      let quality = 0.9;
      let blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      while (blob && blob.size > maxBytes && quality > 0.45) {
        quality -= 0.08;
        blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', quality),
        );
      }
      if (!blob) {
        throw new Error('Unable to process image attachment');
      }
      const filenameRoot = (file.name || 'image').replace(/\.[^/.]+$/, '');
      return new File([blob], `${filenameRoot}.jpg`, {
        type: 'image/jpeg',
      });
    },

    async onEmailTemplateAttachmentSelected(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const allowedExtensions = new Set([
        '.pdf',
        '.png',
        '.jpg',
        '.jpeg',
        '.webp',
        '.gif',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.csv',
        '.txt',
      ]);
      const maxBytes = 8 * 1024 * 1024;
      const name = file.name || '';
      const extension = name.includes('.')
        ? `.${name.split('.').pop().toLowerCase()}`
        : '';
      if (!allowedExtensions.has(extension)) {
        this._toast('Unsupported file type', 'danger');
        event.target.value = '';
        return;
      }
      try {
        let processedFile = file;
        if (file.type.startsWith('image/')) {
          processedFile = await this.processImageAttachmentForTemplate(file);
        }
        if (processedFile.size > maxBytes) {
          this._toast('Attachment must be 8MB or smaller', 'danger');
          event.target.value = '';
          return;
        }
        this.emailTemplatePendingAttachmentFile = processedFile;
        this.emailTemplateRemoveAttachment = false;
        this.currentEmailTemplate.attachment_name = processedFile.name;
        this.currentEmailTemplate.attachment_mime_type =
          processedFile.type || file.type || 'application/octet-stream';
        this.currentEmailTemplate.attachment_size = processedFile.size;
        this.currentEmailTemplate.attachment_url = '';
      } catch (error) {
        console.error('Error processing attachment', error);
        this._toast('Failed to process attachment', 'danger');
        event.target.value = '';
      }
    },

    clearEmailTemplateAttachmentSelection() {
      this.emailTemplatePendingAttachmentFile = null;
      this.emailTemplateRemoveAttachment = true;
      this.currentEmailTemplate.attachment_name = '';
      this.currentEmailTemplate.attachment_mime_type = '';
      this.currentEmailTemplate.attachment_size = 0;
      this.currentEmailTemplate.attachment_url = '';
      const input = document.getElementById('emailTemplateAttachmentInput');
      if (input) input.value = '';
    },

    async syncEmailTemplateAttachment(templateId) {
      if (!templateId) return;
      if (this.emailTemplateRemoveAttachment) {
        const response = await fetch(
          `/calendar/api/email-templates/${templateId}/attachment/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove attachment');
        }
        const template = await response.json();
        this.currentEmailTemplate.attachment_name = template.attachment_name;
        this.currentEmailTemplate.attachment_mime_type =
          template.attachment_mime_type;
        this.currentEmailTemplate.attachment_size = template.attachment_size;
        this.currentEmailTemplate.attachment_url = template.attachment_url;
        this.emailTemplateRemoveAttachment = false;
      }
      if (this.emailTemplatePendingAttachmentFile) {
        const formData = new FormData();
        formData.append('file', this.emailTemplatePendingAttachmentFile);
        const response = await fetch(
          `/calendar/api/email-templates/${templateId}/attachment/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
            body: formData,
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload attachment');
        }
        const template = await response.json();
        this.currentEmailTemplate.attachment_name = template.attachment_name;
        this.currentEmailTemplate.attachment_mime_type =
          template.attachment_mime_type;
        this.currentEmailTemplate.attachment_size = template.attachment_size;
        this.currentEmailTemplate.attachment_url = template.attachment_url;
        this.emailTemplatePendingAttachmentFile = null;
        const input = document.getElementById('emailTemplateAttachmentInput');
        if (input) input.value = '';
      }
    },

    _getPlainTextFromHtml(value) {
      const text = String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return text;
    },

    validateEmailTemplateForm() {
      const name = String(this.currentEmailTemplate?.name || '').trim();
      const subject = String(
        this.currentEmailTemplate?.subject_template || '',
      ).trim();
      const bodyText = this._getPlainTextFromHtml(
        this.currentEmailTemplate?.body_html || '',
      );

      if (!name) return 'Template name is required.';
      if (!subject) return 'Template subject is required.';
      if (!bodyText) return 'Template body is required.';
      return null;
    },

    validateEmailScheduleForm(isSystemImmediateSchedule = false) {
      if (isSystemImmediateSchedule) return null;

      const name = String(this.currentEmailSchedule?.name || '').trim();
      const template = String(this.currentEmailSchedule?.template || '').trim();

      if (!name) return 'Schedule name is required.';
      if (!template) return 'Schedule template is required.';

      if (
        this.currentEmailSchedule?.apply_to_all_ratesets === false &&
        (this.currentEmailSchedule?.rate_set_ids || []).length === 0
      ) {
        return 'Select at least one listing/package or enable "Apply to all listings/packages".';
      }

      if (
        this.currentEmailSchedule?.apply_to_all_partners === false &&
        this.currentEmailSchedule?.include_no_partner === false &&
        (this.currentEmailSchedule?.included_partner_ids || []).length === 0
      ) {
        return 'Select at least one partner or enable "No partner" bookings.';
      }

      const mode = String(
        this.currentEmailSchedule?.payment_link_mode || 'none',
      )
        .trim()
        .toLowerCase();
      if (mode !== 'none') {
        const provider = String(
          this.currentEmailSchedule?.payment_link_provider || 'stripe',
        )
          .trim()
          .toLowerCase();
        if (!['stripe', 'paypal'].includes(provider)) {
          return 'Select a valid payment provider for automated payment links.';
        }
        if (provider === 'paypal' && !this.isPaypalConnectedForPayments) {
          return 'Connect PayPal first to use PayPal payment links.';
        }
        if (provider === 'stripe' && !this.isStripeConnectedForPayments) {
          return 'Connect Stripe first to use Stripe payment links.';
        }
      }

      return null;
    },

    async saveEmailTemplate() {
      this.savingEmailTemplate = true;
      try {
        if (this.emailTemplateQuill) {
          this.currentEmailTemplate.body_html =
            this.emailTemplateQuill.root.innerHTML;
        }

        const templateValidationError = this.validateEmailTemplateForm();
        if (templateValidationError) {
          this._toast(templateValidationError, 'warning');
          return;
        }

        const isUpdate = !!this.currentEmailTemplate.id;
        const url = isUpdate
          ? `/calendar/api/email-templates/${this.currentEmailTemplate.id}/`
          : '/calendar/api/email-templates/';
        const method = isUpdate ? 'PUT' : 'POST';
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(this.currentEmailTemplate),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save template');
        }
        const savedTemplate = await response.json();
        await this.syncEmailTemplateAttachment(savedTemplate.id);
        this.showEmailTemplateEditor = false;
        await this.fetchEmailTemplates();
        this._toast('Email template saved', 'success');
      } catch (error) {
        console.error('Error saving email template:', error);
        this._toast(error.message || 'Failed to save email template', 'danger');
      } finally {
        this.savingEmailTemplate = false;
      }
    },

    async saveTemplate() {
      await this.saveEmailTemplate();
    },

    async sendTemplateTestEmail() {
      if (!this.currentEmailTemplate?.id) {
        this._toast('Save the template first to send a test email', 'warning');
        return;
      }
      this.sendingEmailTemplateTest = true;
      try {
        if (this.emailTemplateQuill) {
          this.currentEmailTemplate.body_html =
            this.emailTemplateQuill.root.innerHTML;
        }

        const templateValidationError = this.validateEmailTemplateForm();
        if (templateValidationError) {
          this._toast(templateValidationError, 'warning');
          return;
        }

        const saveResponse = await fetch(
          `/calendar/api/email-templates/${this.currentEmailTemplate.id}/`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(this.currentEmailTemplate),
          },
        );
        if (!saveResponse.ok) {
          const errorData = await saveResponse.json();
          throw new Error(
            errorData.error || 'Failed to save template before test send',
          );
        }
        await this.syncEmailTemplateAttachment(this.currentEmailTemplate.id);
        const response = await fetch(
          `/calendar/api/email-templates/${this.currentEmailTemplate.id}/send-test/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({}),
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send test email');
        }
        this._toast('Test email sent to owner email', 'success');
      } catch (error) {
        console.error('Error sending test email template:', error);
        this._toast(error.message || 'Failed to send test email', 'danger');
      } finally {
        this.sendingEmailTemplateTest = false;
      }
    },

    async deleteEmailTemplate(templateId) {
      if (!confirm('Delete this template?')) return;
      try {
        const response = await fetch(
          `/calendar/api/email-templates/${templateId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete template');
        }
        await this.fetchEmailTemplates();
        this._toast('Email template deleted', 'success');
      } catch (error) {
        console.error('Error deleting email template:', error);
        this._toast(
          error.message || 'Failed to delete email template',
          'danger',
        );
      }
    },

    async deleteTemplate(templateId) {
      await this.deleteEmailTemplate(templateId);
    },

    getScheduleTriggerUiFromType(triggerType) {
      const mapping = {
        immediate_when_booked: { anchor: 'booked', timing: 'immediate' },
        status_change_immediate: {
          anchor: 'status_change',
          timing: 'immediate',
        },
        after_booked: { anchor: 'booked', timing: 'after' },
        before_start: { anchor: 'start', timing: 'before' },
        after_start: { anchor: 'start', timing: 'after' },
        before_end: { anchor: 'end', timing: 'before' },
        after_end: { anchor: 'end', timing: 'after' },
      };
      return (
        mapping[triggerType] || {
          anchor: 'booked',
          timing: 'after',
        }
      );
    },

    syncScheduleTriggerTypeFromUi() {
      const anchor = this.currentEmailSchedule.trigger_anchor || 'booked';
      const defaultTimingByAnchor = {
        booked: 'after',
        start: 'before',
        end: 'before',
        status_change: 'immediate',
      };
      const validTimingByAnchor = {
        booked: ['immediate', 'after'],
        start: ['before', 'after'],
        end: ['before', 'after'],
        status_change: ['immediate'],
      };
      const fallbackTiming = defaultTimingByAnchor[anchor] || 'after';
      const currentTiming = this.currentEmailSchedule.trigger_timing;
      const timing = (validTimingByAnchor[anchor] || []).includes(currentTiming)
        ? currentTiming
        : fallbackTiming;
      if (this.currentEmailSchedule.trigger_timing !== timing) {
        this.currentEmailSchedule.trigger_timing = timing;
      }
      if (anchor === 'booked' && timing === 'immediate') {
        this.currentEmailSchedule.trigger_type = 'immediate_when_booked';
        this.currentEmailSchedule.offset_days = 0;
        this.currentEmailSchedule.trigger_from_statuses = [];
        return;
      }
      if (anchor === 'status_change') {
        this.currentEmailSchedule.trigger_type = 'status_change_immediate';
        this.currentEmailSchedule.offset_days = 0;
        return;
      }
      if (anchor === 'booked') {
        this.currentEmailSchedule.trigger_type = 'after_booked';
        this.currentEmailSchedule.trigger_from_statuses = [];
        return;
      }
      if (anchor === 'start') {
        this.currentEmailSchedule.trigger_type =
          timing === 'before' ? 'before_start' : 'after_start';
        this.currentEmailSchedule.trigger_from_statuses = [];
        return;
      }
      this.currentEmailSchedule.trigger_type =
        timing === 'before' ? 'before_end' : 'after_end';
      this.currentEmailSchedule.trigger_from_statuses = [];
    },

    onScheduleTriggerAnchorChange(selectedAnchor = null) {
      if (selectedAnchor !== null) {
        this.currentEmailSchedule.trigger_anchor = selectedAnchor;
      }
      if (this.currentEmailSchedule.trigger_anchor === 'booked') {
        const validTiming = ['immediate', 'after'];
        if (!validTiming.includes(this.currentEmailSchedule.trigger_timing)) {
          this.currentEmailSchedule.trigger_timing = 'after';
        }
      } else if (this.currentEmailSchedule.trigger_anchor === 'status_change') {
        this.currentEmailSchedule.trigger_timing = 'immediate';
      } else {
        // Start/end anchors should default to "before" when switching anchor.
        this.currentEmailSchedule.trigger_timing = 'before';
      }
      this.syncScheduleTriggerTypeFromUi();
    },

    onScheduleTriggerTimingChange(selectedTiming = null) {
      if (selectedTiming !== null) {
        this.currentEmailSchedule.trigger_timing = selectedTiming;
      }
      if (this.currentEmailSchedule.trigger_anchor === 'status_change') {
        this.currentEmailSchedule.trigger_timing = 'immediate';
      } else if (
        this.currentEmailSchedule.trigger_anchor !== 'booked' &&
        this.currentEmailSchedule.trigger_timing === 'immediate'
      ) {
        this.currentEmailSchedule.trigger_timing = 'before';
      } else if (this.currentEmailSchedule.trigger_anchor === 'booked') {
        const validTiming = ['immediate', 'after'];
        if (!validTiming.includes(this.currentEmailSchedule.trigger_timing)) {
          this.currentEmailSchedule.trigger_timing = 'after';
        }
      }
      this.syncScheduleTriggerTypeFromUi();
    },

    getScheduleTriggerSummary(schedule) {
      const offsetDays = Number(schedule?.offset_days || 0);
      const triggerAnchor = schedule?.trigger_anchor;
      const triggerTiming = schedule?.trigger_timing;
      if (triggerAnchor && triggerTiming) {
        if (triggerAnchor === 'status_change') {
          const toStatuses = (schedule?.allowed_booking_statuses || [])
            .map((status) => String(status).toUpperCase())
            .join(', ');
          const fromStatuses = (schedule?.trigger_from_statuses || [])
            .map((status) => String(status).toUpperCase())
            .join(', ');
          if (fromStatuses && toStatuses) {
            return `Immediate on status change (${fromStatuses} -> ${toStatuses})`;
          }
          if (toStatuses) {
            return `Immediate on status change (to ${toStatuses})`;
          }
          return 'Immediate on status change';
        }

        if (triggerAnchor === 'booked') {
          if (triggerTiming === 'immediate') {
            return 'Immediate when booked (non-admin only)';
          }
          return `${offsetDays} day${offsetDays === 1 ? '' : 's'} after booked`;
        }

        if (triggerAnchor === 'start') {
          const resolvedTiming = triggerTiming === 'after' ? 'after' : 'before';
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } ${resolvedTiming} start date`;
        }

        if (triggerAnchor === 'end') {
          const resolvedTiming = triggerTiming === 'after' ? 'after' : 'before';
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } ${resolvedTiming} end date`;
        }
      }

      const triggerType = schedule?.trigger_type;
      switch (triggerType) {
        case 'system_immediate': {
          const templateName = String(
            schedule?.template_name || '',
          ).toLowerCase();
          const scheduleName = String(schedule?.name || '').toLowerCase();
          const isManualSendSystem =
            templateName.includes('payment request') ||
            templateName.includes('digital waiver') ||
            scheduleName.includes('payment request') ||
            scheduleName.includes('digital waiver');
          if (isManualSendSystem) {
            return 'Immediate when sent (system)';
          }
          return 'Immediate when booked (system)';
        }
        case 'immediate_when_booked':
          return 'Immediate when booked (non-admin only)';
        case 'status_change_immediate': {
          const toStatuses = (schedule?.allowed_booking_statuses || [])
            .map((status) => String(status).toUpperCase())
            .join(', ');
          const fromStatuses = (schedule?.trigger_from_statuses || [])
            .map((status) => String(status).toUpperCase())
            .join(', ');
          if (fromStatuses && toStatuses) {
            return `Immediate on status change (${fromStatuses} -> ${toStatuses})`;
          }
          if (toStatuses) {
            return `Immediate on status change (to ${toStatuses})`;
          }
          return 'Immediate on status change';
        }
        case 'after_booked':
          return `${offsetDays} day${offsetDays === 1 ? '' : 's'} after booked`;
        case 'before_start':
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } before start date`;
        case 'after_start':
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } after start date`;
        case 'before_end':
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } before end date`;
        case 'after_end':
          return `${offsetDays} day${
            offsetDays === 1 ? '' : 's'
          } after end date`;
        default:
          return triggerType || '';
      }
    },

    // Kept for backward compatibility in case external callers invoke it.
    _legacyOnScheduleTriggerTimingChange() {
      if (
        this.currentEmailSchedule.trigger_anchor !== 'booked' &&
        this.currentEmailSchedule.trigger_timing === 'immediate'
      ) {
        this.currentEmailSchedule.trigger_timing = 'before';
      }
      this.syncScheduleTriggerTypeFromUi();
    },

    scheduleUsesOffset() {
      return !(this.currentEmailSchedule.trigger_timing === 'immediate');
    },

    onScheduleApplyToAllPartnersChange() {
      if (this.currentEmailSchedule.apply_to_all_partners) {
        this.currentEmailSchedule.include_no_partner = true;
        this.currentEmailSchedule.included_partner_ids =
          this.schedulePartners.map((partner) => String(partner.id));
      }
    },

    onScheduleApplyToAllRatesetsChange() {
      if (!this.currentEmailSchedule.apply_to_all_ratesets) {
        const currentSelected = this.currentEmailSchedule.rate_set_ids || [];
        if (currentSelected.length === 0) {
          this.currentEmailSchedule.rate_set_ids = this.scheduleRateSets.map(
            (rateSet) => rateSet.id,
          );
        }
      }
    },

    isEditingSystemImmediateSchedule() {
      return (
        this.currentEmailSchedule &&
        this.currentEmailSchedule.id &&
        this.currentEmailSchedule.trigger_type === 'system_immediate'
      );
    },

    openNewEmailScheduleEditor() {
      this.currentEmailSchedule = {
        id: null,
        name: '',
        template: '',
        trigger_type: 'after_booked',
        trigger_anchor: 'booked',
        trigger_timing: 'after',
        offset_days: 0,
        trigger_from_statuses: [],
        allowed_booking_statuses: [],
        payment_link_mode: 'none',
        payment_link_provider: this.isStripeConnectedForPayments
          ? 'stripe'
          : this.isPaypalConnectedForPayments
          ? 'paypal'
          : 'stripe',
        payment_link_fixed_amount: '',
        payment_link_balance_percentage: '',
        payment_link_expiry_days: 7,
        cancel_pending_on_payment_link_expiry: false,
        send_copy_to_owner: false,
        send_copy_to_shared_accounts: false,
        notify_staff_on_digital_waiver_signed: false,
        apply_to_all_ratesets: true,
        apply_to_all_partners: true,
        rate_set_ids: [],
        include_no_partner: true,
        included_partner_ids: this.schedulePartners.map((partner) =>
          String(partner.id),
        ),
        excluded_partner_ids: [],
        is_active: true,
      };
      this.showEmailScheduleEditor = true;
      setTimeout(() => {
        const el = document.getElementById('emailScheduleEditorCard');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },

    prepareNewSchedule() {
      this.openNewEmailScheduleEditor();
    },

    openEditEmailScheduleEditor(schedule) {
      const triggerUi = this.getScheduleTriggerUiFromType(
        schedule.trigger_type,
      );
      this.currentEmailSchedule = {
        id: schedule.id,
        name: schedule.name,
        template: schedule.template,
        trigger_type: schedule.trigger_type,
        trigger_anchor: triggerUi.anchor,
        trigger_timing: triggerUi.timing,
        offset_days: schedule.offset_days,
        trigger_from_statuses: (schedule.trigger_from_statuses || []).map(
          (status) => String(status).toUpperCase(),
        ),
        allowed_booking_statuses: (schedule.allowed_booking_statuses || []).map(
          (status) => String(status).toUpperCase(),
        ),
        payment_link_mode: schedule.payment_link_mode || 'none',
        payment_link_provider: schedule.payment_link_provider || 'stripe',
        payment_link_fixed_amount: schedule.payment_link_fixed_amount || '',
        payment_link_balance_percentage:
          schedule.payment_link_balance_percentage || '',
        payment_link_expiry_days: Number(
          schedule.payment_link_expiry_days || 7,
        ),
        cancel_pending_on_payment_link_expiry:
          schedule.cancel_pending_on_payment_link_expiry === true,
        send_copy_to_owner: schedule.send_copy_to_owner === true,
        send_copy_to_shared_accounts:
          schedule.send_copy_to_shared_accounts === true,
        notify_staff_on_digital_waiver_signed:
          schedule.notify_staff_on_digital_waiver_signed === true,
        template_name: schedule.template_name || '',
        apply_to_all_ratesets: schedule.apply_to_all_ratesets,
        apply_to_all_partners:
          schedule.include_no_partner !== false &&
          (schedule.excluded_partners || []).length === 0,
        rate_set_ids: (schedule.rate_sets || []).map((item) => item.id),
        include_no_partner: schedule.include_no_partner !== false,
        included_partner_ids: this.schedulePartners
          .map((partner) => String(partner.id))
          .filter(
            (partnerId) =>
              !(schedule.excluded_partners || []).some(
                (excluded) => String(excluded.id) === String(partnerId),
              ),
          ),
        excluded_partner_ids: (schedule.excluded_partners || []).map(
          (item) => item.id,
        ),
        is_active: schedule.is_active,
      };
      this.showEmailScheduleEditor = true;
      setTimeout(() => {
        const el = document.getElementById('emailScheduleEditorCard');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },

    editSchedule(schedule) {
      this.openEditEmailScheduleEditor(schedule);
    },

    async saveEmailSchedule() {
      this.savingEmailSchedule = true;
      try {
        const isSystemImmediateSchedule =
          this.currentEmailSchedule.trigger_type === 'system_immediate';
        if (!isSystemImmediateSchedule) {
          this.syncScheduleTriggerTypeFromUi();
        }

        const scheduleValidationError = this.validateEmailScheduleForm(
          isSystemImmediateSchedule,
        );
        if (scheduleValidationError) {
          this._toast(scheduleValidationError, 'warning');
          return;
        }

        const isUpdate = !!this.currentEmailSchedule.id;
        const url = isUpdate
          ? `/calendar/api/email-schedules/${this.currentEmailSchedule.id}/`
          : '/calendar/api/email-schedules/';
        const method = isUpdate ? 'PUT' : 'POST';
        const allPartnerIds = this.schedulePartners.map((partner) =>
          String(partner.id),
        );
        const includedPartnerIds = (
          this.currentEmailSchedule.included_partner_ids || []
        ).map((id) => String(id));
        const payload = {
          template: this.currentEmailSchedule.template,
          name: this.currentEmailSchedule.name,
          trigger_type: this.currentEmailSchedule.trigger_type,
          offset_days: this.scheduleUsesOffset()
            ? Number(this.currentEmailSchedule.offset_days || 0)
            : 0,
          is_active: this.currentEmailSchedule.is_active !== false,
          apply_to_all_ratesets:
            this.currentEmailSchedule.apply_to_all_ratesets !== false,
          allowed_booking_statuses: (
            this.currentEmailSchedule.allowed_booking_statuses || []
          ).map((status) => String(status).toUpperCase()),
          trigger_from_statuses: (this.currentEmailSchedule.trigger_anchor ===
          'status_change'
            ? this.currentEmailSchedule.trigger_from_statuses || []
            : []
          ).map((status) => String(status).toUpperCase()),
          payment_link_mode:
            this.currentEmailSchedule.payment_link_mode || 'none',
          payment_link_provider:
            this.currentEmailSchedule.payment_link_mode !== 'none'
              ? String(
                  this.currentEmailSchedule.payment_link_provider || 'stripe',
                ).toLowerCase()
              : 'stripe',
          payment_link_fixed_amount:
            this.currentEmailSchedule.payment_link_mode === 'fixed_amount' &&
            this.currentEmailSchedule.payment_link_fixed_amount !== ''
              ? Number(this.currentEmailSchedule.payment_link_fixed_amount)
              : null,
          payment_link_balance_percentage:
            this.currentEmailSchedule.payment_link_mode ===
              'percentage_balance' &&
            this.currentEmailSchedule.payment_link_balance_percentage !== ''
              ? Number(
                  this.currentEmailSchedule.payment_link_balance_percentage,
                )
              : null,
          payment_link_expiry_days: Number(
            this.currentEmailSchedule.payment_link_expiry_days || 7,
          ),
          cancel_pending_on_payment_link_expiry:
            this.currentEmailSchedule.payment_link_mode !== 'none' &&
            this.currentEmailSchedule.cancel_pending_on_payment_link_expiry ===
              true,
          send_copy_to_owner:
            this.currentEmailSchedule.send_copy_to_owner === true,
          send_copy_to_shared_accounts:
            this.currentEmailSchedule.send_copy_to_shared_accounts === true,
          notify_staff_on_digital_waiver_signed:
            this.currentEmailSchedule.notify_staff_on_digital_waiver_signed ===
            true,
          rate_set_ids: (this.currentEmailSchedule.rate_set_ids || []).map(
            (id) => Number(id),
          ),
          include_no_partner:
            this.currentEmailSchedule.include_no_partner !== false,
          excluded_partner_ids:
            this.currentEmailSchedule.apply_to_all_partners !== false
              ? []
              : allPartnerIds
                  .filter(
                    (partnerId) =>
                      !includedPartnerIds.includes(String(partnerId)),
                  )
                  .map((id) => String(id)),
        };
        if (this.currentEmailSchedule.apply_to_all_partners !== false) {
          payload.include_no_partner = true;
        }
        if (isSystemImmediateSchedule) {
          payload.template = undefined;
          payload.name = undefined;
          payload.trigger_type = undefined;
          payload.offset_days = undefined;
          payload.is_active = undefined;
          payload.apply_to_all_ratesets = undefined;
          payload.allowed_booking_statuses = undefined;
          payload.trigger_from_statuses = undefined;
          payload.rate_set_ids = undefined;
          payload.include_no_partner = undefined;
          payload.excluded_partner_ids = undefined;
          const templateName = String(
            this.currentEmailSchedule.template_name || '',
          ).toLowerCase();
          const isDigitalWaiverSystemSchedule =
            templateName.includes('digital waiver');
          if (!isDigitalWaiverSystemSchedule) {
            payload.notify_staff_on_digital_waiver_signed = undefined;
          }
        }
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save schedule');
        }
        this.showEmailScheduleEditor = false;
        await this.fetchEmailSchedules();
        this._toast('Email schedule saved', 'success');
      } catch (error) {
        console.error('Error saving email schedule:', error);
        this._toast(error.message || 'Failed to save email schedule', 'danger');
      } finally {
        this.savingEmailSchedule = false;
      }
    },

    async saveSchedule() {
      await this.saveEmailSchedule();
    },

    async deleteEmailSchedule(scheduleId) {
      if (!confirm('Delete this schedule?')) return;
      try {
        const response = await fetch(
          `/calendar/api/email-schedules/${scheduleId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete schedule');
        }
        await this.fetchEmailSchedules();
        this._toast('Email schedule deleted', 'success');
      } catch (error) {
        console.error('Error deleting email schedule:', error);
        this._toast(
          error.message || 'Failed to delete email schedule',
          'danger',
        );
      }
    },

    async deleteSchedule(scheduleId) {
      await this.deleteEmailSchedule(scheduleId);
    },
  };

  // Preserve getter/setter descriptors (Object.assign would flatten them).
  Object.defineProperties(
    messagingStore,
    Object.getOwnPropertyDescriptors(messagingExtensions),
  );

  return Alpine.store('calendarSettingsMessagingStore', messagingStore);
}
